export type ResearchProject = {
  id: string;
  title: string;
  kicker: string;
  summary: string;
  intro: string[];
  tags: string[];
  cover: string;
  metrics: { value: string; label: string }[];
  plan?: string[];
  status: 'active' | 'planned';
  kind: 'direction' | 'more';
};

export const researchProjects: ResearchProject[] = [
  {
    id: 'shipborne',
    title: '船载碳捕集系统',
    kicker: 'Shipborne CCS',
    summary: '在有限甲板空间与船舶工况下，把吸收、解吸与余热利用集成到一套紧凑系统里。',
    intro: [
      '面向船舶烟气余热有限、负荷随航行变化的实际条件，课题组搭建了紧凑船载碳捕集中试台架，把再沸器与解吸塔集成，直接回收烟气余热，并用实船烟气数据设计试验工况。',
      '研究以液气比和解吸压力为主要调节量，分析捕集率、再生热与循环容量之间的平衡；进一步把系统与 LNG 冷能 ORC 耦合，探索零增耗捕集与 CO₂ 液化的可行路径。',
    ],
    tags: ['船舶尾气', '紧凑布置', '余热利用', 'ORC 耦合'],
    cover: '/research/covers/abstract-shipborne.svg',
    metrics: [
      { value: '88.6%', label: '最高捕集效率' },
      { value: '62.5%', label: '再生热最大降幅' },
      { value: '3.92', label: '最低再生热 MJ/kgCO₂' },
    ],
    status: 'active',
    kind: 'direction',
  },
  {
    id: 'absorption',
    title: '强化吸收',
    kicker: 'Enhanced Absorption',
    summary: '拟开展方向：围绕吸收段传质限制，研究吸收剂、喷淋与填料结构对捕集效率的影响。',
    intro: [
      '该方向目前处于方案设计与试验准备阶段，尚未发表专门文章。计划以 MEA 为基液，比较吸收剂配方、纳米流体与塔内件结构对吸收速率和负荷适应性的影响。',
      '拟在现有 CCS 试验台上搭建吸收段对比实验，先建立可复现的测试方法与评价指标，再逐步推进论文与工程放大。',
    ],
    tags: ['吸收剂', '填料结构', '传质强化'],
    cover: '/research/covers/abstract-absorption.svg',
    metrics: [],
    plan: [
      '吸收剂与纳米流体配方筛选',
      '填料、喷淋密度与液气比优化',
      '吸收段传质与压降测试方法',
      '与解吸段的耦合和放大',
    ],
    status: 'planned',
    kind: 'direction',
  },
  {
    id: 'desorption',
    title: '强化解吸',
    kicker: 'Enhanced Desorption',
    summary: '拟开展方向：围绕解吸能耗与沸腾传热，研究表面改性、工艺参数与部件集成。',
    intro: [
      '该方向目前处于方案设计与试验准备阶段，尚未发表专门文章。计划围绕富液沸腾解吸中的气泡脱离、局部热阻与再生能耗展开。',
      '拟从表面改性、解吸压力与贫富液换热三条路径入手，并在部件侧探索一体化再沸器与紧凑解吸塔的集成方式。',
    ],
    tags: ['再生能耗', '表面改性', '工艺优化', '一体化再沸器'],
    cover: '/research/covers/abstract-desorption.svg',
    metrics: [],
    plan: [
      '富液沸腾解吸表面改性（仿生微结构、润湿性）',
      '解吸压力与贫富液换热参数优化',
      '一体化再沸器与紧凑解吸塔',
      '再生能耗与捕集率协同评价',
    ],
    status: 'planned',
    kind: 'direction',
  },
  {
    id: 'pche',
    title: 'PCHE 印刷电路板式换热器',
    kicker: 'Printed Circuit Heat Exchanger',
    summary: '面向紧凑高效换热，研究锯齿通道 PCHE 在低温汽化与碳捕集流程中的适用性。',
    intro: [
      '面向 LNG 船 FGSS 低温汽化器与碳捕集系统的紧凑换热需求，课题组搭建了 214×56.5×41.5 mm 锯齿通道 PCHE 低温试验台，以液氮和 50% 乙二醇水溶液为工质。',
      '实验考察质量流量、进口温度与顺/逆流布置对换热能力和压降的影响，重点分析局部冻结条件下传热恶化与压降上升的机理，为 PCHE 在船舶与碳捕集流程中的应用提供部件基础。',
    ],
    tags: ['微通道', '紧凑换热', '低温汽化', '压降'],
    cover: '/research/covers/abstract-pche.svg',
    metrics: [
      { value: '1.1–1.8 kW', label: '逆流换热增益' },
      { value: '214×56.5×41.5', label: '试验段尺寸 mm' },
      { value: '3 篇', label: '相关研究' },
    ],
    status: 'active',
    kind: 'direction',
  },
  {
    id: 'extra',
    title: '其他研究进展',
    kicker: 'More Research',
    summary: '船用电池热管理、航运排放核算，以及吸收-解吸与数据驱动优化的阶段性工作。',
    intro: [
      '除四个主方向外，课题组还在高容量船用电池热管理、港口船舶排放清单与智能代理模型，以及吸收-解吸与数据驱动优化等方向持续开展工作。',
    ],
    tags: ['电池热管理', '航运排放', '吸收-解吸', '数据驱动'],
    cover: '/research/covers/extra.webp',
    metrics: [
      { value: '6 篇', label: '相关研究' },
      { value: '280 Ah', label: '船用电池' },
      { value: '500 m', label: '排放清单网格' },
    ],
    status: 'active',
    kind: 'more',
  },
  {
    id: 'news',
    title: '课题组动态',
    kicker: 'Lab News',
    summary: '获奖、会议、团队活动与人才培养的最新消息。',
    intro: ['课题组的获奖、学术会议、团队活动与人才培养动态。'],
    tags: ['获奖', '会议', '团队'],
    cover: '/research/covers/news.webp',
    metrics: [
      { value: '5 篇', label: '最新动态' },
      { value: '3 位', label: '全球前 2% 科学家' },
      { value: '2026', label: '科协人才计划' },
    ],
    status: 'active',
    kind: 'more',
  },
];

export const directionProjects = researchProjects.filter((project) => project.kind === 'direction');
export const moreProjects = researchProjects.filter((project) => project.kind === 'more');

export function getResearchProject(id: string | undefined): ResearchProject | null {
  return researchProjects.find((project) => project.id === id) ?? null;
}